/**
 * 将下划线后跟小写字母的单词转换为驼峰命名
 * 
 * @param key - 需要转换的字符串
 * @returns 转换后的驼峰命名字符串
 * 
 * @example
 * ```ts
 * underscoreToCamelCase('status_code'); // => 'statusCode'
 * underscoreToCamelCase('user_name'); // => 'userName'
 * underscoreToCamelCase('api_key'); // => 'apiKey'
 * ```
 */
function underscoreToCamelCase(key: string = ''): string {
  return key.replace(/_[a-z]{1}/g, match => match.replace(/_/, '').toUpperCase());
}

/**
 * 递归将所有下划线方式的属性名替换为驼峰命名
 *
 * 该函数深度遍历对象和数组，将所有属性名从下划线命名转换为驼峰命名。
 * 对于字符串输入，直接进行驼峰转换。
 * 常用于处理后端 API 返回的数据格式转换。
 *
 * @param data - 需要转换的数据，可以是对象、数组、字符串或原始值
 * @returns 转换后的数据，保持原始数据结构不变
 *
 * @example
 * ```ts
 * import { convertToCamelCase } from '@fu/matrix';
 *
 * // 字符串转换
 * convertToCamelCase('user_name'); // => 'userName'
 * convertToCamelCase('api_key_secret'); // => 'apiKeySecret'
 *
 * // 对象转换
 * const apiData = {
 *   user_name: 'John',
 *   email_address: 'john@example.com',
 *   created_at: '2023-01-01'
 * };
 * convertToCamelCase(apiData);
 * // => { userName: 'John', emailAddress: 'john@example.com', createdAt: '2023-01-01' }
 *
 * // 数组转换
 * const userList = [
 *   { user_id: 1, user_name: 'Alice' },
 *   { user_id: 2, user_name: 'Bob' }
 * ];
 * convertToCamelCase(userList);
 * // => [{ userId: 1, userName: 'Alice' }, { userId: 2, userName: 'Bob' }]
 *
 * // 嵌套对象转换
 * const complexData = {
 *   user_info: {
 *     basic_info: {
 *       first_name: 'John',
 *       last_name: 'Doe'
 *     },
 *     contact_list: [
 *       { phone_number: '123-456-7890' }
 *     ]
 *   }
 * };
 * convertToCamelCase(complexData);
 * // => {
 * //   userInfo: {
 * //     basicInfo: {
 * //       firstName: 'John',
 * //       lastName: 'Doe'
 * //     },
 * //     contactList: [
 * //       { phoneNumber: '123-456-7890' }
 * //     ]
 * //   }
 * // }
 * ```
 *
 * @since 1.4.0
 */
export function convertToCamelCase<T = any>(data: T): T {
  const type = Object.prototype.toString.call(data);

  if (type === '[object String]') {
    // 处理字符串 - 直接进行转换
    return underscoreToCamelCase(data as string) as T;
  } else if (type === '[object Array]') {
    // 处理数组 - 递归处理数组元素
    return (data as any[]).map(item => convertToCamelCase(item)) as T;
  } else if (type === '[object Object]') {
    // 处理对象 - 只转换属性名，不转换字符串值
    const result = Object.create(null);

    for (const property in data) {
      if (Object.prototype.hasOwnProperty.call(data, property)) {
        const camelCaseKey = underscoreToCamelCase(property);
        const value = (data as any)[property];
        // 对于字符串值，不进行转换；对于其他类型，递归转换
        result[camelCaseKey] = typeof value === 'string' ? value : convertToCamelCase(value);
      }
    }

    return result as T;
  } else {
    // 其他类型直接返回
    return data;
  }
}